import type { InfiniteData } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type {
  AddMemberRequest,
  ChatDto,
  ChatDtoPaginationResultDto,
  ChatReplyRequest,
  ChatStateChangeRequest,
  CreateGroupChatRequest,
  RemoveMemberRequest,
  UpdateGroupChatRequest,
} from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";
import type { ApiResponseType } from "@/types/api-types";

import { MAX_PINNED_CHATS } from "./constants";

const useAddPrivateChat = () => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => api.postChatsPrivateV2({ userId }).then((x) => x.data),
    onSuccess: (_, userId) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USER_CHAT_DETAILS(projectId, userId) });
    },
    onError: () => {
      console.error("Error creating private chat!");
    },
  });
};

const useDeleteChat = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const projectId = useProjectId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chatId: string) => api.deleteChatsByIdV2(chatId),
    onSuccess: (_, chatId) => {
      showFlashToast({ variant: "success", message: t("page.chats.delete-chat.success") });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CHATS_LIST(projectId) });

      queryClient.removeQueries({
        queryKey: QUERY_KEYS.CHATS_REPLIES(projectId, chatId),
      });
    },
  });
};

const useAddChatReply = () => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ chatId, data }: { chatId: string; data: ChatReplyRequest }) =>
      api.postChatsReplyV2(chatId, data).then((x) => x.data),
    onSuccess: async (_, payload) => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CHATS_REPLIES(projectId, payload.chatId) });
    },
    onError() {
      showFlashToast({ variant: "alert", message: t("page.user-detail.chat.send-error") });
    },
  });
};

const useLeaveGroupChat = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const projectId = useProjectId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chatId: string) => api.postChatsMembersLeaveV2(chatId),
    onSuccess: (_, chatId) => {
      showFlashToast({ variant: "success", message: t("component.group-chat-leave-modal.success") });

      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CHATS_LIST(projectId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CHATS_MEMBERS(projectId, chatId) });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("component.group-chat-leave-modal.error"),
      });
    },
  });
};

const useUpdateGroupChat = (chatId: string) => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateGroupChatRequest) => api.putChatsGroupV2(chatId, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CHATS_DETAILS(projectId, chatId) });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CHATS_LIST(projectId) });
    },
  });
};

const useRemoveGroupChatMember = (chatId: string) => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: RemoveMemberRequest) => api.putChatsMembersRemoveV2(chatId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CHATS_MEMBERS(projectId, chatId) });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("component.group-chat-remove-user-modal.error"),
      });
    },
  });
};

const useUpdateChatPermissionState = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const projectId = useProjectId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { chatPermission: Exclude<ChatStateChangeRequest["chatPermission"], undefined> }) =>
      api.postChatsStateV1(payload),
    onMutate: (payload) => {
      queryClient.setQueryData<ApiResponseType<"getSelfV2"> | undefined>(QUERY_KEYS.SELF(projectId), (oldResponse) => {
        if (oldResponse) {
          return {
            ...oldResponse,
            data: {
              ...oldResponse.data,
              chatPermission: payload.chatPermission,
            },
          };
        }
      });
    },
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SELF(projectId) });
    },
    onError() {
      showFlashToast({ variant: "alert", message: t("page.chats.toggle-chats.error") });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SELF(projectId) });
    },
  });
};

const useBlockUser = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const projectId = useProjectId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => api.postChatsBlocksV2(userId),
    onSuccess: (_, userId) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USER_CHAT_DETAILS(projectId, userId) });

      showFlashToast({
        variant: "success",
        message: t("page.user-detail.block-private-chat-from-user.notification.success"),
      });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.user-detail.block-private-chat-from-user.notification.error"),
      });
    },
  });
};

const useUnblockUser = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const projectId = useProjectId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => api.deleteChatsBlocksByIdV2(userId),
    onSuccess: (_, userId) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USER_CHAT_DETAILS(projectId, userId) });

      showFlashToast({
        variant: "success",
        message: t("page.user-detail.unblock-private-chat-from-user.notification.success"),
      });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.user-detail.unblock-private-chat-from-user.notification.error"),
      });
    },
  });
};

const usePinChat = () => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (chatId: string) => api.postChatsPinV2(chatId),
    onMutate: async (chatId) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.CHATS_LIST(projectId) });

      const previousData = queryClient.getQueriesData<InfiniteData<ChatDtoPaginationResultDto>>({
        queryKey: QUERY_KEYS.CHATS_LIST(projectId),
      });

      queryClient.setQueriesData<InfiniteData<ChatDtoPaginationResultDto>>(
        { queryKey: QUERY_KEYS.CHATS_LIST(projectId) },
        (oldData) => {
          if (!oldData) return oldData;

          const allItems = oldData.pages.flatMap((page) => page.items);

          if (allItems.filter((chat) => Boolean(chat.pinnedAt)).length >= MAX_PINNED_CHATS) {
            return oldData;
          }

          const updatedItems = allItems.map((chat) =>
            chat.id === chatId ? { ...chat, pinnedAt: new Date().toISOString() } : chat,
          );

          return applySort(oldData, updatedItems);
        },
      );

      return { previousData };
    },
    onError: (_, __, context) => {
      context?.previousData.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      showFlashToast({
        variant: "alert",
        message: t("page.chats.pin-chat.notification.error"),
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CHATS_LIST(projectId) });
    },
  });
};

const useUnpinChat = () => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (chatId: string) => api.deleteChatsPinV2(chatId),
    onMutate: async (chatId) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.CHATS_LIST(projectId) });

      const previousData = queryClient.getQueriesData<InfiniteData<ChatDtoPaginationResultDto>>({
        queryKey: QUERY_KEYS.CHATS_LIST(projectId),
      });

      queryClient.setQueriesData<InfiniteData<ChatDtoPaginationResultDto>>(
        { queryKey: QUERY_KEYS.CHATS_LIST(projectId) },
        (oldData) => {
          if (!oldData) return oldData;

          const allItems = oldData.pages.flatMap((page) => page.items);
          const updatedItems = allItems.map((chat) => {
            if (chat.id !== chatId) return chat;
            const { pinnedAt, ...rest } = chat;

            return rest satisfies ChatDto;
          });

          return applySort(oldData, updatedItems);
        },
      );

      return { previousData };
    },
    onError: (_, __, context) => {
      context?.previousData.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      showFlashToast({
        variant: "alert",
        message: t("page.chats.unpin-chat.notification.error"),
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CHATS_LIST(projectId) });
    },
  });
};

const useCreateGroupChat = () => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: CreateGroupChatRequest) => api.postChatsGroupV2(payload).then(({ data }) => data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.CHATS_LIST(projectId),
      });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.chats.create-chat.group.error"),
      });
    },
  });
};

const useAddGroupChatMembers = (chatId: string) => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: AddMemberRequest) => api.putChatsMembersAddV2(chatId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CHATS_MEMBERS(projectId, chatId) });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("component.group-chat-info-modal.add-user.error"),
      });
    },
  });
};

export const chatMutations = {
  useAddPrivateChat,
  useDeleteChat,
  useAddChatReply,
  useUpdateGroupChat,
  useUpdateChatPermissionState,
  useLeaveGroupChat,
  useRemoveGroupChatMember,
  useBlockUser,
  useUnblockUser,
  useCreateGroupChat,
  useAddGroupChatMembers,
  usePinChat,
  useUnpinChat,
};

function applySort(
  oldData: InfiniteData<ChatDtoPaginationResultDto>,
  updatedItems: ChatDto[],
): InfiniteData<ChatDtoPaginationResultDto> {
  const sorted = sortChats(updatedItems);
  const pageSizes = oldData.pages.map((page) => page.items.length);
  let offset = 0;

  return {
    ...oldData,
    pages: oldData.pages.map((page, i) => ({ ...page, items: sorted.slice(offset, (offset += pageSizes[i])) })),
  };
}

function sortChats(items: ChatDto[]): ChatDto[] {
  const pinned = items
    .filter((c) => c.pinnedAt)
    .toSorted((a, b) => new Date(b.pinnedAt!).getTime() - new Date(a.pinnedAt!).getTime());
  const unpinned = items
    .filter((c) => !c.pinnedAt)
    .toSorted((a, b) => {
      if (a.latestReply && b.latestReply) {
        return new Date(b.latestReply.sentAt).getTime() - new Date(a.latestReply.sentAt).getTime();
      }

      if (a.latestReply) {
        return -1;
      }

      if (b.latestReply) {
        return 1;
      }

      return 0;
    });

  return [...pinned, ...unpinned];
}
